import { OrderedSet, Set } from 'immutable';
import { combineLatest, Observable, of } from 'rxjs';
import { distinctUntilChanged, map, switchMap } from 'rxjs/operators';
import { ViewModelService } from 'src/app/shared/view-model.service';
import { CardStatus, UnitStatus } from 'src/graphql/generated/graphql.generated';
import { selectCardSummariesByRegions } from 'src/store/entityStores/card-selectors';
import { selectCommunicationCentersByCompany } from 'src/store/entityStores/communication-center-selectors';
import {
  selectOperationsRegionsByCompanyId,
  selectRegionsByCompanyId,
} from 'src/store/entityStores/region-selectors';
import {
  selectUnitSummariesByRegions,
  selectUnitSummariesByRegionsAndUnits,
} from 'src/store/entityStores/unit-selectors';
import { CompanyRecord, CompanyViewModelBase } from 'src/store/generated/company-record.generated';
import { createObservableLookup } from 'src/util/lookup';
import { isNullOrEmpty } from 'src/util/string';

export class CompanyViewModel extends CompanyViewModelBase {
  public regionIds$: Observable<Set<string>>;
  public regionsIdsWithoutAssignedCommCenters$: Observable<OrderedSet<string>>;
  public regionsIdsWithoutAssignedCommCentersCount$: Observable<number>;
  public communicationCenterIds$: Observable<Set<string>>;

  constructor(company: CompanyRecord, private _model: ViewModelService) {
    super(company);
    const operationRegions$ = _model.store.select(selectOperationsRegionsByCompanyId(this.id));
    this.regionIds$ = operationRegions$.pipe(
      map((regions) => regions.map((r) => r.id).toSet()),
      distinctUntilChanged(),
    );
    this.regionsIdsWithoutAssignedCommCenters$ = operationRegions$.pipe(
      map((regions) =>
        regions
          .filter((r) => r.communicationCenterId == null)
          .sort((a, b) => a.abbreviation.localeCompare(b.abbreviation))
          .map((r) => r.id)
          .toOrderedSet(),
      ),
      distinctUntilChanged(),
    );

    this.regionsIdsWithoutAssignedCommCentersCount$ =
      this.regionsIdsWithoutAssignedCommCenters$.pipe(
        map((c) => c.count()),
        distinctUntilChanged(),
      );

    this.communicationCenterIds$ = _model.store
      .select(selectCommunicationCentersByCompany(this.id))
      .pipe(
        map((ccs) => ccs.map((cc) => cc.id).toSet()),
        distinctUntilChanged(),
      );
  }

  public get companyBadgeClass(): string {
    const companyCodeClass = this.companyCode == null ? '' : ` company--${this.companyCode}`;
    return `company-badge${companyCodeClass}`;
  }

  public allUnitIdsByStatus$(
    regionIds: string[] | null,
    nameOrTailLike?: string,
    includeHidden = false,
    includeSpareUnits = false,
  ) {
    return createObservableLookup(UnitStatus, (unitStatus) => {
      let regionIds$: Observable<string[]>;
      if (regionIds != null && regionIds.length > 0) {
        regionIds$ = of(regionIds);
      } else {
        regionIds$ = this._getAllRegionIds();
      }

      const units$ = regionIds$.pipe(
        switchMap((ids) =>
          this._model.store.select(
            selectUnitSummariesByRegions(
              ids,
              true,
              nameOrTailLike,
              false,
              unitStatus as UnitStatus,
            ),
          ),
        ),
      );

      return units$.pipe(
        map((units) =>
          units
            .filter((u) =>
              includeHidden
                ? true
                : u.status === UnitStatus.OutOfService
                ? true
                : !(u.isHidden ?? false),
            )
            .filter((u) => (includeSpareUnits ? true : !(u.isSpare ?? false)))
            .sort((a, b) => a.abbreviation.localeCompare(b.abbreviation))
            .map((u) => u.id)
            .toOrderedSet(),
        ),
      );
    });
  }

  public allUnitIds$(regionIds: string[], nameOrTailLike?: string, includeHidden = false) {
    return this._model.store.select(
      selectUnitSummariesByRegions(regionIds, includeHidden, nameOrTailLike, false),
    );
  }

  public cardIdsByStatus$(
    regionIds: string[] | null,
    unitNameOrTailLike?: string,
    filterCardIds: string[] = [],
  ) {
    return createObservableLookup(CardStatus, (cardStatus) => {
      let regionIds$: Observable<string[]>;
      if (regionIds != null && regionIds.length > 0) {
        regionIds$ = of(regionIds);
      } else {
        regionIds$ = this._getAllRegionIds();
      }

      const cards$ = regionIds$.pipe(
        switchMap((ids) =>
          this._model.store.select(selectCardSummariesByRegions(ids, cardStatus as CardStatus)),
        ),
      );

      const units$ = combineLatest([
        regionIds$,
        cards$.pipe(map((cards) => cards.filter((c) => filterCardIds.includes(c.id)))),
      ]).pipe(
        switchMap(([rIds, cIds]) => {
          const unitIds = cIds
            .map((c) => c.unitId)
            .filter((id) => id != null)
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            .map((id) => id!)
            .toJSON();
          return this._model.store.select(
            selectUnitSummariesByRegionsAndUnits(rIds, unitIds, true, unitNameOrTailLike, true),
          );
        }),
      );

      const result$ = combineLatest([cards$, units$]).pipe(
        map(([cards, units]) => {
          if (isNullOrEmpty(unitNameOrTailLike)) {
            return cards;
          }

          const opsCards = units.map((u) => cards.find((c) => u != null && c.unitId === u.id));

          return (
            opsCards
              .filter((c) => c != null)
              // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
              .map((c) => c!)
              .sortBy((c) => c.cardNumber)
          );
        }),
      );

      return result$.pipe(
        distinctUntilChanged(),
        map((cards) =>
          cards
            .sort((a, b) => {
              return a.dateCreated < b.dateCreated ? -1 : 1;
            })
            .map((c) => c.id)
            .toOrderedSet(),
        ),
        distinctUntilChanged(),
      );
    });
  }

  private _getAllRegionIds() {
    return this._getAllRegions().pipe(
      map((regions) =>
        regions
          .valueSeq()
          .map((r) => r.id)
          .toArray(),
      ),
    );
  }

  private _getAllRegions() {
    return this._model.store.select(selectRegionsByCompanyId(this.id));
  }
}
